import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import './GuidedRun.css';

const GuidedRun = ({ onClose, onSendMessage }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [datasetPreview, setDatasetPreview] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [sapResults, setSapResults] = useState(null);
  const [rCode, setRCode] = useState('');
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sapLoading, setSapLoading] = useState(false);
  const [rCodeLoading, setRCodeLoading] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [rtfViewer, setRtfViewer] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState('ancova');

  // Available studies
  const studies = [
    {
      id: 'pilot1',
      name: 'R Consortium R Submission Pilot 1 to FDA',
      description: 'CDISC Pilot Study - Testing R-based submissions to FDA',
      url: 'https://github.com/RConsortium/submissions-pilot1-to-fda',
      datasets: ['adsl.xpt', 'adadas.xpt', 'adlbc.xpt', 'adtte.xpt', 'adae.xpt'],
      documents: ['sap.pdf', 'protocol.pdf', 'report-tlf-pilot3.pdf', 'csr.pdf', 'tlf.pdf']
    }
  ];

  useEffect(() => {
    // Load datasets and documents when study is selected
    if (selectedStudy) {
      loadDatasets();
      loadDocuments();
    }
  }, [selectedStudy]);

  const backendBase = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8000`;

  const loadDatasets = async () => {
    try {
      console.log('Loading datasets...');
      const response = await fetch(`${backendBase}/api/datasets`);
      const data = await response.json();
      console.log('Datasets loaded:', data);
      setDatasets(data.datasets || []);
    } catch (error) {
      console.error('Error loading datasets:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      console.log('Loading documents...');
      const response = await fetch(`${backendBase}/api/documents`);
      const data = await response.json();
      console.log('Documents loaded:', data);
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const handleDatasetClick = async (dataset) => {
    setSelectedDataset(dataset);
    setLoading(true);
    try {
      const response = await fetch(`${backendBase}/api/dataset-preview/${dataset.name}`);
      const data = await response.json();
      setDatasetPreview(data);
    } catch (error) {
      console.error('Error loading dataset preview:', error);
    }
    setLoading(false);
  };

  const handleDocumentClick = async (document) => {
    setSelectedDocument(document);
    setDocumentPreview({ name: document.name, type: 'pdf' });
  };

  const handleSapExtraction = async () => {
    setSapLoading(true);
    try {
      const questionTexts = {
        'ancova': 'Generate a table for ANCOVA of Change from Baseline at Week 20',
        'demographics': 'Create a table for Summary of Demographic and Baseline Characteristics',
        'adas': 'Generate a table for Primary Endpoint Analysis: ADAS Cog (11) – Change from Baseline to Week 24 – LOCF',
        'km_plot': 'Generate a KM plot for the Time to Dermatologic Event: Safety population by Treatment Group'
      };
      
      const response = await fetch(`${backendBase}/api/sap-extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          request: questionTexts[selectedQuestion] || questionTexts['ancova']
        })
      });
      const data = await response.json();
      setSapResults(data);
    } catch (error) {
      console.error('Error extracting SAP:', error);
    }
    setSapLoading(false);
  };

  const generateRCode = async () => {
    setRCodeLoading(true);
    try {
      // Add a 10-second delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 10000));
      
      const analysisTypes = {
        'ancova': 'ANCOVA of Change from Baseline at Week 20',
        'demographics': 'Summary of Demographic and Baseline Characteristics',
        'adas': 'Primary Endpoint Analysis: ADAS Cog (11) – Change from Baseline to Week 24 – LOCF',
        'km_plot': 'KM plot for the Time to Dermatologic Event: Safety population by Treatment Group'
      };
      
      const response = await fetch(`${backendBase}/api/generate-r-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          analysis: analysisTypes[selectedQuestion] || analysisTypes['ancova'],
          question_type: selectedQuestion
        })
      });
      const data = await response.json();
      setRCode(data.code);
    } catch (error) {
      console.error('Error generating R code:', error);
    }
    setRCodeLoading(false);
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const analysisTypes = {
        'ancova': 'ANCOVA of Change from Baseline at Week 20',
        'demographics': 'Summary of Demographic and Baseline Characteristics',
        'adas': 'Primary Endpoint Analysis: ADAS Cog (11) – Change from Baseline to Week 24 – LOCF',
        'km_plot': 'KM plot for the Time to Dermatologic Event: Safety population by Treatment Group'
      };
      
      const response = await fetch(`${backendBase}/api/run-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          code: rCode,
          analysis: analysisTypes[selectedQuestion] || analysisTypes['ancova'],
          question_type: selectedQuestion
        })
      });
      const data = await response.json();
      setAnalysisResults(data);
    } catch (error) {
      console.error('Error running analysis:', error);
    }
    setLoading(false);
  };

  const viewRtfFile = async (filename) => {
    try {
      const response = await fetch(`${backendBase}/api/view-rtf/${filename}`);
      const data = await response.json();
      setRtfViewer(data);
    } catch (error) {
      console.error('Error viewing RTF file:', error);
    }
  };

  const closeRtfViewer = () => {
    setRtfViewer(null);
  };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 7));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const renderStep1 = () => (
    <div className="guided-step">
      <h3>📋 Step 1: Select Study</h3>
      <p>Choose the clinical trial study you want to work with:</p>
      
      <div className="study-cards">
        {studies.map(study => (
          <div 
            key={study.id}
            className={`study-card ${selectedStudy?.id === study.id ? 'selected' : ''}`}
            onClick={() => setSelectedStudy(study)}
          >
            <div className="study-header">
              <h4>{study.name}</h4>
              <span className="study-badge">Pilot Study</span>
            </div>
            <p className="study-description">{study.description}</p>
            <div className="study-links">
              <a href={study.url} target="_blank" rel="noopener noreferrer">
                🔗 View on GitHub
              </a>
            </div>
            <div className="study-assets">
              <span>📊 {study.datasets.length} Datasets</span>
              <span>📄 {study.documents.length} Documents</span>
            </div>
          </div>
        ))}
      </div>
      
      <button 
        className="next-btn"
        onClick={nextStep}
        disabled={!selectedStudy}
      >
        Next: Explore Datasets →
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="guided-step">
      <h3>📊 Step 2: Explore Datasets</h3>
      <p>Click on any dataset to preview its structure and data:</p>
      
      {/* Debug info */}
      <div className="debug-info" style={{marginBottom: '20px', padding: '10px', borderRadius: '5px'}}>
        <strong>Debug Info:</strong> Found {datasets.length} datasets
        {datasets.length > 0 && (
          <ul>
            {datasets.map(d => <li key={d.name}>{d.name}</li>)}
          </ul>
        )}
      </div>
      
      <div className="dataset-grid">
        {datasets.map(dataset => (
          <div 
            key={dataset.name}
            className={`dataset-card ${selectedDataset?.name === dataset.name ? 'selected' : ''}`}
            onClick={() => handleDatasetClick(dataset)}
          >
            <div className="dataset-icon">📊</div>
            <div className="dataset-info">
              <h4>{dataset.name}</h4>
              <p>{dataset.description}</p>
              <span className="dataset-size">{dataset.size}</span>
            </div>
          </div>
        ))}
      </div>

      {datasetPreview && (
        <div className="preview-container">
          <h4>Dataset Preview: {datasetPreview.name}</h4>
          <div className="table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  {datasetPreview.columns?.slice(0, 10).map((col, i) => (
                    <th key={i}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datasetPreview.sampleData?.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    {row.slice(0, 10).map((cell, j) => (
                      <td key={j}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="step-nav">
        <button className="prev-btn" onClick={prevStep}>← Previous</button>
        <button className="next-btn" onClick={nextStep}>Next: Explore Documents →</button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="guided-step">
      <h3>📄 Step 3: Explore Documents</h3>
      <p>Click on any document to preview or download:</p>
      
      {/* Debug info */}
      <div className="debug-info" style={{marginBottom: '20px', padding: '10px', borderRadius: '5px'}}>
        <strong>Debug Info:</strong> Found {documents.length} documents
        {documents.length > 0 && (
          <ul>
            {documents.map(d => <li key={d.name}>{d.name}</li>)}
          </ul>
        )}
      </div>
      
      <div className="document-grid">
        {documents.map(document => (
          <div 
            key={document.name}
            className={`document-card ${selectedDocument?.name === document.name ? 'selected' : ''}`}
            onClick={() => handleDocumentClick(document)}
          >
            <div className="document-icon">📄</div>
            <div className="document-info">
              <h4>{document.name}</h4>
              <p>{document.description}</p>
              <span className="document-size">{document.size}</span>
            </div>
          </div>
        ))}
      </div>

      {documentPreview && (
        <div className="preview-container">
          <h4>Document: {documentPreview.name}</h4>
          <div className="document-preview">
            <iframe 
              src={`${backendBase}/api/document-view/${documentPreview.name}`}
              width="100%" 
              height="400px"
              title={documentPreview.name}
            />
          </div>
        </div>
      )}
      
      <div className="step-nav">
        <button className="prev-btn" onClick={prevStep}>← Previous</button>
        <button className="next-btn" onClick={nextStep}>Next: Start Analysis →</button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="guided-step">
      <h3>🎯 Step 4: Define Analysis Question</h3>
      <p>Select the analysis question you want to explore:</p>
      
      <div className="question-selection">
        <div 
          className={`question-card ${selectedQuestion === 'ancova' ? 'selected' : ''}`} 
          onClick={() => setSelectedQuestion('ancova')}
        >
          <h4>📊 ANCOVA Analysis</h4>
          <p><strong>Generate a table for ANCOVA of Change from Baseline at Week 20</strong></p>
          <p>This analysis will examine the change from baseline in laboratory values at Week 20 using Analysis of Covariance (ANCOVA) with baseline values as covariates.</p>
          <div className="question-details">
            <span className="question-type">Primary Analysis</span>
            <span className="question-complexity">Advanced</span>
          </div>
        </div>
        
        <div 
          className={`question-card ${selectedQuestion === 'demographics' ? 'selected' : ''}`} 
          onClick={() => setSelectedQuestion('demographics')}
        >
          <h4>👥 Demographics Summary</h4>
          <p><strong>Create a table for Summary of Demographic and Baseline Characteristics</strong></p>
          <p>Generate comprehensive demographic and baseline characteristic tables including age, gender, race, and baseline measurements.</p>
          <div className="question-details">
            <span className="question-type">Descriptive</span>
            <span className="question-complexity">Basic</span>
          </div>
        </div>
        
        <div 
          className={`question-card ${selectedQuestion === 'adas' ? 'selected' : ''}`} 
          onClick={() => setSelectedQuestion('adas')}
        >
          <h4>🧠 ADAS Cog Analysis</h4>
          <p><strong>Generate a table for Primary Endpoint Analysis: ADAS Cog (11) – Change from Baseline to Week 24 – LOCF</strong></p>
          <p>Primary endpoint analysis for Alzheimer's Disease Assessment Scale-Cognitive subscale using Last Observation Carried Forward methodology.</p>
          <div className="question-details">
            <span className="question-type">Primary Endpoint</span>
            <span className="question-complexity">Advanced</span>
          </div>
        </div>
        
        <div 
          className={`question-card ${selectedQuestion === 'km_plot' ? 'selected' : ''}`} 
          onClick={() => setSelectedQuestion('km_plot')}
        >
          <h4>📈 Kaplan-Meier Plot</h4>
          <p><strong>Generate a KM plot for the Time to Dermatologic Event: Safety population by Treatment Group</strong></p>
          <p>Survival analysis plot showing time to dermatologic events with confidence intervals and treatment group comparisons.</p>
          <div className="question-details">
            <span className="question-type">Survival Analysis</span>
            <span className="question-complexity">Advanced</span>
          </div>
        </div>
      </div>
      
      <div className="selected-question-info">
        <p><strong>Selected:</strong> {selectedQuestion}</p>
      </div>
      
      <div className="step-nav">
        <button className="prev-btn" onClick={prevStep}>← Previous</button>
        <button className="next-btn" onClick={nextStep}>Next: Extract SAP →</button>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="guided-step">
      <h3>📋 Step 5: Extract SAP Information</h3>
      <p>Extract the relevant Statistical Analysis Plan information for our analysis:</p>
      
      <div className="sap-section">
        <button 
          className="sap-btn"
          onClick={handleSapExtraction}
          disabled={sapLoading}
        >
          {sapLoading ? (
            <>
              <span className="spinner"></span>
              <span>Extracting SAP...</span>
            </>
          ) : (
            '📋 Extract SAP Information'
          )}
        </button>
        
        {sapLoading && (
          <div className="sap-loading">
            <div className="loading-message">
              <span className="spinner"></span>
              <p>🤖 AI is analyzing the SAP document...</p>
              <p className="loading-subtitle">This may take 10-20 seconds</p>
            </div>
          </div>
        )}
        
        {sapResults && (
          <div className="sap-results">
            <h4>📋 SAP Results:</h4>
            <div className="sap-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]} 
                rehypePlugins={[rehypeRaw]}
                components={{
                  h1: ({children}) => <h1 style={{color: 'var(--text-color)', marginTop: '20px', marginBottom: '10px'}}>{children}</h1>,
                  h2: ({children}) => <h2 style={{color: 'var(--text-color)', marginTop: '16px', marginBottom: '8px'}}>{children}</h2>,
                  h3: ({children}) => <h3 style={{color: 'var(--text-color)', marginTop: '12px', marginBottom: '6px'}}>{children}</h3>,
                  p: ({children}) => <p style={{color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '8px'}}>{children}</p>,
                  ul: ({children}) => <ul style={{color: 'var(--text-secondary)', paddingLeft: '20px', marginBottom: '10px'}}>{children}</ul>,
                  ol: ({children}) => <ol style={{color: 'var(--text-secondary)', paddingLeft: '20px', marginBottom: '10px'}}>{children}</ol>,
                  li: ({children}) => <li style={{marginBottom: '4px'}}>{children}</li>,
                  strong: ({children}) => <strong style={{color: 'var(--accent-color)', fontWeight: 'bold'}}>{children}</strong>,
                  code: ({children}) => <code style={{backgroundColor: 'var(--secondary-background)', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace'}}>{children}</code>,
                  pre: ({children}) => <pre style={{backgroundColor: 'var(--secondary-background)', padding: '12px', borderRadius: '6px', overflow: 'auto', border: '1px solid var(--border-color)'}}>{children}</pre>
                }}
              >
                {sapResults.content}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>
      
      <div className="step-nav">
        <button className="prev-btn" onClick={prevStep}>← Previous</button>
        <button 
          className="next-btn" 
          onClick={nextStep}
          disabled={!sapResults}
        >
          Next: Generate R Code →
        </button>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="guided-step">
      <h3>💻 Step 6: Generate R Code</h3>
      <p>Generate the R code based on the SAP requirements:</p>
      
      <div className="code-generation">
        <button 
          className="generate-btn"
          onClick={generateRCode}
          disabled={rCodeLoading}
        >
          {rCodeLoading ? (
            <>
              <span className="spinner"></span>
              <span>Generating R Code...</span>
            </>
          ) : (
            '⚡ Generate R Code'
          )}
        </button>
        
        {rCodeLoading && (
          <div className="r-code-loading">
            <div className="loading-message">
              <span className="spinner"></span>
              <p>🤖 AI is generating R code based on SAP...</p>
              <p className="loading-subtitle">Creating optimized analysis code (10 seconds)</p>
            </div>
          </div>
        )}
        
        {rCode && (
          <div className="code-results">
            <div className="code-header">
              <h4>📝 Generated R Code:</h4>
              <div className="code-actions">
                <button 
                  className="toggle-code-btn"
                  onClick={() => setShowCode(!showCode)}
                >
                  {showCode ? '📖 Hide Raw Code' : '👁️ Show Raw Code'}
                </button>
                <button 
                  className="copy-code-btn"
                  onClick={() => navigator.clipboard.writeText(rCode)}
                >
                  📋 Copy Code
                </button>
              </div>
            </div>
            <div className="code-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]} 
                rehypePlugins={[rehypeRaw]}
                components={{
                  pre: ({children}) => <pre className="r-code-pre">{children}</pre>,
                  code: ({children}) => <code className="r-code-inline">{children}</code>
                }}
              >
                {`\`\`\`r\n${rCode}\n\`\`\``}
              </ReactMarkdown>
            </div>
            {showCode && (
              <div className="raw-code-section">
                <h5>Raw R Code:</h5>
                <pre className="raw-code">{rCode}</pre>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="step-nav">
        <button className="prev-btn" onClick={prevStep}>← Previous</button>
        <button 
          className="next-btn" 
          onClick={nextStep}
          disabled={!rCode}
        >
          Next: Run Analysis →
        </button>
      </div>
    </div>
  );

  const renderStep7 = () => {
    const getAnalysisTitle = () => {
      const titles = {
        'ancova': 'ANCOVA Analysis',
        'demographics': 'Demographics Summary',
        'adas': 'ADAS Cog Analysis',
        'km_plot': 'Kaplan-Meier Plot'
      };
      return titles[selectedQuestion] || 'Analysis';
    };

    const getTableTitle = () => {
      const titles = {
        'ancova': 'ANCOVA Results Table',
        'demographics': 'Demographics Summary Table',
        'adas': 'ADAS Cog Results Table',
        'km_plot': 'Survival Analysis Results'
      };
      return titles[selectedQuestion] || 'Results Table';
    };

    return (
      <div className="guided-step">
        <h3>🚀 Step 7: Run Analysis</h3>
        <p>Execute the R code to generate the {getAnalysisTitle().toLowerCase()}:</p>
        
        <div className="analysis-execution">
          <button 
            className="run-btn"
            onClick={runAnalysis}
            disabled={loading}
          >
            {loading ? '🔄 Running Analysis...' : '▶️ Run Analysis'}
          </button>
          
          {analysisResults && (
            <div className="analysis-results">
              <h4>📊 Analysis Results:</h4>
              <div className="results-content">
                {/* R Output */}
                {analysisResults.output && (
                  <div className="r-output-section">
                    <h5>🔍 R Console Output:</h5>
                    <div className="output-container">
                      <pre className="r-output">{analysisResults.output}</pre>
                    </div>
                  </div>
                )}
                
                {/* Plot Display for KM Plot */}
                {selectedQuestion === 'km_plot' && analysisResults.plot && (
                  <div className="plot-section">
                    <h5>📈 Kaplan-Meier Plot:</h5>
                    <div className="plot-container">
                      <img 
                        src={`data:image/png;base64,${analysisResults.plot}`}
                        alt="Kaplan-Meier Plot"
                        className="analysis-plot"
                      />
                    </div>
                  </div>
                )}
                
                {/* Results Table - Skip for demographics, ADAS, ANCOVA and KM plot */}
                {selectedQuestion !== 'demographics' && selectedQuestion !== 'adas' && selectedQuestion !== 'ancova' && selectedQuestion !== 'km_plot' && analysisResults.table && (
                  <div className="results-table">
                    <h5>📋 {getTableTitle()}:</h5>
                    <div className="table-container">
                      <table className="results-table-content">
                        <thead>
                          <tr>
                            {analysisResults.table.headers?.map((header, i) => (
                              <th key={i}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {analysisResults.table.rows?.map((row, i) => (
                            <tr key={i}>
                              {row.map((cell, j) => (
                                <td key={j}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}


                {/* ANCOVA RTF Document Preview */}
                {selectedQuestion === 'ancova' && analysisResults.files && analysisResults.files.length > 0 && (
                  <div className="ancova-rtf-preview">
                    <h5>📄 ANCOVA Analysis Document:</h5>
                    <div className="rtf-document-viewer">
                      {analysisResults.files
                        .filter(file => file.name.endsWith('.rtf'))
                        .slice(0, 1) // Only show the first RTF file
                        .map((file, i) => (
                          <div key={i} className="rtf-document-container">
                            <div className="document-header">
                              <span className="document-icon">📄</span>
                              <span className="document-title">{file.name}</span>
                              <a 
                                href={`${backendBase}/api/download/${file.name}`}
                                download={file.name}
                                className="download-document-btn"
                              >
                                📥 Download
                              </a>
                            </div>
                            <div className="document-content">
                              <iframe
                                src={`${backendBase}/api/preview-rtf/${file.name}`}
                                className="rtf-iframe"
                                title="RTF Document Preview"
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* ADAS RTF Document Preview */}
                {selectedQuestion === 'adas' && analysisResults.files && analysisResults.files.length > 0 && (
                  <div className="adas-rtf-preview">
                    <h5>📄 ADAS Cog Analysis Document:</h5>
                    <div className="rtf-document-viewer">
                      {analysisResults.files
                        .filter(file => file.name.endsWith('.rtf'))
                        .slice(0, 1) // Only show the first RTF file
                        .map((file, i) => (
                          <div key={i} className="rtf-document-container">
                            <div className="document-header">
                              <span className="document-icon">📄</span>
                              <span className="document-title">{file.name}</span>
                              <a 
                                href={`${backendBase}/api/download/${file.name}`}
                                download={file.name}
                                className="download-document-btn"
                              >
                                📥 Download
                              </a>
                            </div>
                            <div className="document-content">
                              <iframe
                                src={`${backendBase}/api/preview-rtf/${file.name}`}
                                className="rtf-iframe"
                                title="RTF Document Preview"
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* Generated Files - Skip for demographics, ADAS, ANCOVA and KM plot */}
                {analysisResults.files && analysisResults.files.length > 0 && selectedQuestion !== 'km_plot' && selectedQuestion !== 'demographics' && selectedQuestion !== 'adas' && selectedQuestion !== 'ancova' && (
                  <div className="generated-files">
                    <h5>📁 Generated Files:</h5>
                    <div className="files-list">
                      {analysisResults.files.map((file, i) => (
                        <div key={i} className="result-file">
                          <div className="file-info">
                            <span className="file-icon">📄</span>
                            <span className="file-name">{file.name}</span>
                            <span className="file-type">
                              {file.name.endsWith('.png') ? 'PNG Image' : 'RTF Document'}
                            </span>
                          </div>
                          <div className="file-actions">
                            {file.name.endsWith('.png') ? (
                              <a 
                                href={`${backendBase}/api/download/${file.name}`}
                                download={file.name}
                                className="download-link"
                              >
                                📥 Download Plot
                              </a>
                            ) : (
                              <>
                                <button 
                                  className="view-file-btn"
                                  onClick={() => viewRtfFile(file.name)}
                                >
                                  👁️ View
                                </button>
                                <a
                                  href={`${backendBase}/api/download/${file.name}`}
                                  download={file.name}
                                  className="download-link"
                                >
                                  📥 Download
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="step-nav">
          <button className="prev-btn" onClick={prevStep}>← Previous</button>
          <button className="complete-btn" onClick={onClose}>✅ Complete</button>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      default: return renderStep1();
    }
  };

  return (
    <div className="guided-run-overlay">
      <div className="guided-run-modal">
        <div className="guided-run-header">
          <h2>🎯 Guided Analysis Run</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="progress-bar">
          {[1, 2, 3, 4, 5, 6, 7].map(step => (
            <div 
              key={step}
              className={`progress-step ${step <= currentStep ? 'active' : ''} ${step === currentStep ? 'current' : ''}`}
            >
              <span className="step-number">{step}</span>
              <span className="step-label">
                {step === 1 ? 'Study' : 
                 step === 2 ? 'Datasets' :
                 step === 3 ? 'Documents' :
                 step === 4 ? 'Question' :
                 step === 5 ? 'SAP' :
                 step === 6 ? 'Code' :
                 step === 7 ? 'Run' : ''}
              </span>
            </div>
          ))}
        </div>
        
        <div className="guided-run-content">
          {renderCurrentStep()}
        </div>
      </div>
      
      {/* RTF Viewer Modal */}
      {rtfViewer && (
        <div className="rtf-viewer-modal">
          <div className="rtf-viewer-content">
            <div className="rtf-viewer-header">
              <h3>📄 RTF Document Viewer</h3>
              <button className="close-rtf-btn" onClick={closeRtfViewer}>
                ✕
              </button>
            </div>
            <div className="rtf-viewer-body">
              <div className="rtf-file-info">
                <strong>File:</strong> {rtfViewer.filename}<br/>
                <strong>Size:</strong> {(rtfViewer.size / 1024).toFixed(1)} KB
              </div>
              <div className="rtf-content">
                <pre>{rtfViewer.content}</pre>
              </div>
            </div>
            <div className="rtf-viewer-footer">
              <a 
                href={`${backendBase}/api/download/${rtfViewer.filename}`}
                download={rtfViewer.filename}
                className="download-link"
              >
                📥 Download RTF File
              </a>
              <button className="close-rtf-btn" onClick={closeRtfViewer}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedRun;
